// Bottom-sheet ancestor picker. Shows the full path from root to the
// current folder as a vertical list, indented to suggest hierarchy.
// Tapping any row navigates to that level; the current folder is
// shown selected but not tappable.

import { Home, ChevronRight, Check } from "lucide-react";
import { BottomSheet } from "@/components/ui/bottom-sheet";
import { cn } from "@/lib/utils";

interface BreadcrumbSheetProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Path segments from root (exclusive) to current folder (inclusive). */
  segments: string[];
  /** Called with a 0-indexed segment; -1 means root. Closes the sheet. */
  onJump: (index: number) => void;
}

export function BreadcrumbSheet({
  open,
  onOpenChange,
  segments,
  onJump,
}: BreadcrumbSheetProps) {
  const jump = (index: number) => {
    onOpenChange(false);
    onJump(index);
  };

  const currentIndex = segments.length - 1;

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title="Navigate to…">
      <ul className="flex flex-col">
        <Row
          depth={0}
          onClick={() => jump(-1)}
          selected={currentIndex === -1}
          icon={<Home className="h-4 w-4" />}
          label="root"
        />
        {segments.map((seg, i) => (
          <Row
            key={i}
            depth={i + 1}
            onClick={() => jump(i)}
            selected={i === currentIndex}
            icon={<ChevronRight className="h-4 w-4" />}
            label={decodeURIComponent(seg)}
          />
        ))}
      </ul>
    </BottomSheet>
  );
}

function Row({
  depth,
  onClick,
  selected,
  icon,
  label,
}: {
  depth: number;
  onClick: () => void;
  selected: boolean;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        disabled={selected}
        style={{ paddingLeft: `${12 + depth * 16}px` }}
        className={cn(
          "flex w-full items-center gap-2 py-3 pr-4 text-left text-text-soft hover:bg-accent-bg hover:text-text",
          selected && "font-medium text-text",
        )}
      >
        <span className="text-text-dim">{icon}</span>
        <span className="flex-1 truncate">{label}</span>
        {selected && <Check className="h-4 w-4 text-accent" />}
      </button>
    </li>
  );
}
